/**
 * Real User Monitoring (RUM) for Core Web Vitals.
 * Reports LCP, FCP, CLS, INP, TTFB to GA4 via GTM dataLayer so we can
 * cross-reference with CrUX field data in Looker Studio / GA4 explorations.
 *
 * Event name: `web_vitals` (custom GA4 event)
 * Parameters:
 *  - metric_name: LCP | FCP | CLS | INP | TTFB
 *  - metric_value: rounded numeric value (ms, or CLS*1000)
 *  - metric_rating: good | needs-improvement | poor
 *  - metric_id: unique per page load (for de-duplication)
 *  - metric_navigation_type: navigate | reload | back_forward | prerender
 *  - page_path
 */
import { pushToDataLayer } from '@/lib/gtm';
import { onLCP, onFCP, onCLS, onINP, onTTFB, type Metric } from 'web-vitals';


const sendToDataLayer = (metric: Metric) => {
  const value = metric.name === 'CLS'
    ? Math.round(metric.value * 1000)
    : Math.round(metric.value);

  const payload = {
    event: 'web_vitals',
    metric_name: metric.name,
    metric_value: value,
    metric_rating: metric.rating,
    metric_id: metric.id,
    metric_navigation_type: metric.navigationType,
    metric_delta: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  };

  pushToDataLayer(payload);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[web-vitals] ${metric.name}=${value} (${metric.rating})`);
  }
};

let started = false;

export const startWebVitalsReporting = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    onLCP(sendToDataLayer);
    onFCP(sendToDataLayer);
    onCLS(sendToDataLayer);
    onINP(sendToDataLayer);
    onTTFB(sendToDataLayer);
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[web-vitals] init failed', err);
  }
};
